import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useGiftContext } from "@icoach/gift/components/GiftContext";
import {
	OverviewListLayout,
	OverviewListToolbar,
	OverviewListActionBar,
	OverviewListSingleActions,
	OverviewListBatchActions,
	BatchActionDeleteGiftRecord,
	BatchActionSendMessage,
	PageSortAndTotalBox,
} from "@icoach/components/overviewList";
import { Grid } from "@mui/material";
import { DateTimeStandard, Icons, DateIntervalField, MenuItem, SelectField, TextField } from "@curves/ui-base";
import { MemberSecureCard, OverflowMenu, rowLabelCheckboxSheet, useLabelCheckBox } from "@icoach/components";
import { parseDate } from "@util/moment";
import useMessageDialog from "@util/hook/useMessageDialog";
import DismissibleTooltip from "@icoach/components/DismissibleTooltip";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import useGiftItemRecordApi from "@apis/useGiftItemRecordApi";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import { isEmpty, isFunction } from "@util/utils";
import { getInitialPagination } from "@icoach/gift/staticData";

const GiftDispatchRecordPage = () => {
	const defaultSortBy = 1;
	const { dialogRef, setRefresh } = useGiftContext();
	const setMessageDialog = useMessageDialog();
	const { getGiftItemRecordOptionsApi, getGiftItemRecordOverviewApi, putGiftItemClaimedApi } = useGiftItemRecordApi();
	const { options } = useOptions({ getOptionsApi: async () => await getGiftItemRecordOptionsApi({ isAll: true }), isFetchOnMount: true });
	const { overviewData, searchParams, getOverviewData, updateOverviewPage, updateOverviewSort } = useOverview({
		getOverviewApi: getGiftItemRecordOverviewApi,
		defaultSearchParams: {
			...getInitialPagination(defaultSortBy),
			member: "",
			giftName: "",
			activityID: 99,
			startDate: "",
			endDate: "",
			claimStatus: 99,
		},
		enableInfiniteScroll: true,
		isFetchOnMount: true,
	});

	const { checkBoxsRef, headerCheckBoxRef, onHeaderChange, onCellChange, getCheckedItems, getCheckedSourceData, doSaveCheckedItem, doClearCheckedItems } = useLabelCheckBox();

	const { isRevealed, toggle, renderPrivacyIcon } = usePrivacyToggle();

	const memberRef = useRef(null);
	const giftNameRef = useRef(null);
	const eventNameRef = useRef(null);
	const periodRef = useRef(null);
	const claimStatusRef = useRef(null);

	const getParamsData = () => {
		const result = { ...getInitialPagination(defaultSortBy) };

		if (memberRef.current && memberRef.current.getResult) {
			Object.assign(result, { member: memberRef.current.getResult() });
		}

		if (giftNameRef.current && giftNameRef.current.getResult) {
			Object.assign(result, { giftName: giftNameRef.current.getResult() });
		}

		if (eventNameRef.current && eventNameRef.current.getResult) {
			Object.assign(result, { activityID: eventNameRef.current.getResult() });
		}

		if (periodRef.current && periodRef.current.getResult) {
			Object.assign(result, periodRef.current.getResult());
		}

		if (claimStatusRef.current && claimStatusRef.current.getResult) {
			Object.assign(result, { claimStatus: claimStatusRef.current.getResult() });
		}

		return result;
	};

	const handleSubmit = () => {
		const params = getParamsData();
		doClearCheckedItems();
		getOverviewData(params);
	};

	const handlePageChange = (e, page) => {
		doSaveCheckedItem();
		updateOverviewPage(page);
	};

	const handleSortChange = value => {
		doClearCheckedItems();
		updateOverviewSort(value);
	};

	const handleBatchActionsRefresh = () => {
		doClearCheckedItems();
		getOverviewData(searchParams);
	};

	const handleToggleRevealed = useCallback(
		() => {
			const apiParams = {
				filterType: securityMaskFilterTypeMap.ClubGiftItemRecordOverview,
				totalCount: overviewData?.list?.length,
			};
			toggle(apiParams);
		},
		// eslint-disable-next-line
		[toggle, overviewData.list],
	);

	const headerRow = useMemo(
		() => {
			const getOverflowMenuData = (rowData, refresh, dialogRef) => {
				const { isGiftClaimed, recordID } = rowData;
				const giftClaimedText = isGiftClaimed ? "取消領取" : "領取";

				let menuData = [
					{
						text: giftClaimedText,
						onClick: () => {
							const handleGiftItemClaimedApi = async (params, refresh, onClose) => {
								const { recordID } = params;

								try {
									let resp = await putGiftItemClaimedApi(recordID, params);
									if (resp) {
										isFunction(refresh) && refresh();
										isFunction(onClose) && onClose();
									}
								} catch (error) {
									console.error("Failed to update gift item claimed status:", error);
								}
							};

							setMessageDialog({
								open: true,
								title: "提醒",
								msg: `確定要改為「${giftClaimedText}」狀態嗎？`,
								onAsyncConfirm: (_, onclose) =>
									handleGiftItemClaimedApi(
										{
											recordID,
											isGiftClaimed: !isGiftClaimed,
										},
										refresh,
										onclose,
									),
							});
						},
					},
				];

				if (!isGiftClaimed) {
					menuData = [
						...menuData,
						{
							text: "刪除",
							onClick: () =>
								dialogRef.current && isFunction(dialogRef.current.openBatchDeleteGiftRecordsDialog) && dialogRef.current.openBatchDeleteGiftRecordsDialog([rowData], refresh),
						},
					];
				}

				return menuData;
			};

			return [
				{ ...rowLabelCheckboxSheet, isFixed: true, isFixedLeft: true },
				{
					cellKey: "memberNo",
					headerLabel: "受贈人",
					formatCell: (memberNo, { displayName, maskDisplayName, memberPicturePath, nickName }, { isRevealed }) => {
						return <MemberSecureCard name={displayName} maskName={maskDisplayName} avatar={memberPicturePath} memberNo={memberNo} nickname={nickName} isRevealed={isRevealed} />;
					},
				},
				{
					cellKey: "eventNameText",
					headerLabel: "活動名稱",
					align: "center",
				},
				{
					cellKey: "giftName",
					headerLabel: "贈品名稱",
					align: "center",
				},
				{
					cellKey: "giftSpecifications",
					align: "center",
					headerLabel: "規格",
				},
				{
					cellKey: "claimDate",
					headerLabel: "發送日期",
					formatCell: cellValue => {
						return (
							<React.Fragment>
								<p className="mb-0">{parseDate(cellValue, DateTimeStandard.DisplayDate, false)}</p>
								<p className="mb-0 font-color-3">{parseDate(cellValue, DateTimeStandard.DisplayTime, false)}</p>
							</React.Fragment>
						);
					},
				},
				{
					cellKey: "isGiftClaimed",
					headerLabel: "是否領取",
					align: "center",
					formatCell: (isGiftClaimed, { claimDate }) => {
						return isGiftClaimed ? (
							<DismissibleTooltip tip={parseDate(claimDate, DateTimeStandard.DisplayFullBySlash, false)}>
								<Icons.Check color={"success"} />
							</DismissibleTooltip>
						) : (
							""
						);
					},
				},
				{
					cellKey: "more",
					headerLabel: "",
					width: 6.8,
					formatCell: (value, rowData, { refresh }) => {
						return <OverflowMenu menuData={getOverflowMenuData(rowData, refresh, dialogRef)} />;
					},
				},
			];
		},
		// eslint-disable-next-line
		[],
	);

	useEffect(
		() => {
			setRefresh(getOverviewData);
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<OverviewListLayout>
			<OverviewListLayout.SearchBar className={"bg-light"} onSubmit={handleSubmit} isShowActions>
				<Grid spacing={2} container>
					<Grid xs={4} item>
						<TextField label="受贈人" key={searchParams?.member} ref={memberRef} defaultValue={searchParams?.member} InputProps={{ placeholder: "輸入會員姓名或編號" }} fullWidth />
					</Grid>
					<Grid xs={4} item>
						<TextField label="贈品名稱" key={searchParams?.giftName} ref={giftNameRef} defaultValue={searchParams?.giftName} InputProps={{ placeholder: "輸入贈品名稱" }} fullWidth />
					</Grid>
					<Grid xs={4} item>
						<SelectField key={searchParams?.activityID} ref={eventNameRef} label="活動名稱" defaultValue={searchParams?.activityID} fullWidth required>
							{!isEmpty(options?.giftActivityOption) &&
								options?.giftActivityOption.map(({ text, value }) => (
									<MenuItem key={value} value={value}>
										{text}
									</MenuItem>
								))}
						</SelectField>
					</Grid>
					<Grid xs={8} item>
						<DateIntervalField
							key={`${searchParams?.startDate}-${searchParams?.endDate}`}
							ref={periodRef}
							label="發送期間"
							symbol={"~"}
							startDateProps={{
								defaultValue: searchParams?.startDate || "",
							}}
							endDateProps={{
								defaultValue: searchParams?.endDate || "",
							}}
							fullWidth
						/>
					</Grid>
					<Grid xs={4} item>
						<SelectField key={searchParams?.claimStatus} ref={claimStatusRef} label="領取狀態" defaultValue={searchParams?.claimStatus} fullWidth required>
							{!isEmpty(options?.giftClaimStatusOption) &&
								options?.giftClaimStatusOption.map(({ text, value }) => (
									<MenuItem key={value} value={value}>
										{text}
									</MenuItem>
								))}
						</SelectField>
					</Grid>
				</Grid>
			</OverviewListLayout.SearchBar>
			<OverviewListToolbar>
				<OverviewListActionBar>
					<OverviewListBatchActions headerCheckBoxRef={headerCheckBoxRef} onHeaderChange={onHeaderChange}>
						<BatchActionSendMessage getCheckedSourceData={getCheckedSourceData} openDialog={dialogRef.current && dialogRef.current.openSMSDialog} refresh={handleBatchActionsRefresh} />
						<BatchActionDeleteGiftRecord
							getCheckedSourceData={getCheckedSourceData}
							openDialog={dialogRef.current && dialogRef.current.openBatchDeleteGiftRecordsDialog}
							refresh={handleBatchActionsRefresh}
						/>
					</OverviewListBatchActions>
					<OverviewListSingleActions>{renderPrivacyIcon(handleToggleRevealed)}</OverviewListSingleActions>
				</OverviewListActionBar>
				<PageSortAndTotalBox
					pageIndex={overviewData?.pagination?.pageIndex}
					pageSize={overviewData?.pagination?.pageSize}
					totalCount={overviewData?.pagination?.totalCount}
					isCurrentPageLastIndex
					sortDefaultValue={searchParams?.sortBy || defaultSortBy}
					sortOptions={options?.orderByOption}
					sortOnChange={handleSortChange}
					isSort
				/>
			</OverviewListToolbar>
			<OverviewListLayout.Table
				headerRow={headerRow}
				list={overviewData?.list || []}
				bodyRowClass={{
					key: "enabled",
					value: false,
					className: "overview-item-invalid",
				}}
				totalPage={overviewData?.pagination?.totalPage}
				pageIndex={overviewData?.pagination?.pageIndex}
				onChangePage={handlePageChange}
				refresh={() => getOverviewData(searchParams)}
				tableRowBodyProps={{
					checkBoxsRef: checkBoxsRef,
					onCheckBoxChange: onCellChange,
					getCheckedItems: getCheckedItems,
					isRevealed,
				}}
				isMasonryPagination
				isGray
			/>
		</OverviewListLayout>
	);
};

export default GiftDispatchRecordPage;
